import { useCallback, useEffect, useMemo, useState } from "react"
import { useObjectsCategories } from "hooks/api/objectsCategories/objectsCategories.hook"
import { dedupeOptions, hasOption } from "helpers/categories.helper"
import type { ICategoriesData } from "types/configuration.type"
import type { DefaultOptionType } from "antd/es/select"

type CategoryValue = string | number | null

type UseCategorySelectionParams = {
	defaultTopLevelCategory?: string | null
	defaultSuperCategory?: string | null
	defaultModelCategoryId?: number | null
	defaultModelCategoryName?: string | null
	extraTopLevelOptions?: DefaultOptionType[]
	/**
	 * Top-level categories to keep OUT of the dropdown, by name — for a form that only covers part of
	 * the taxonomy (the "add cabinet" panel of REQ-293 excludes Countertops and Other, which have
	 * their own + Add entries). Applied to the options only: a default coming from the entity itself
	 * is still offered, so an existing item is never silently stripped of its own category.
	 */
	excludedTopLevelCategories?: string[]
}

const optionKey = (option: DefaultOptionType) =>
	String(option.value ?? "")
		.trim()
		.toLowerCase()

const EMPTY_EXTRA_TOP_LEVEL_OPTIONS: DefaultOptionType[] = []
const EMPTY_EXCLUDED_TOP_LEVEL_CATEGORIES: string[] = []

export const useCategorySelection = ({
	defaultTopLevelCategory = null,
	defaultSuperCategory = null,
	defaultModelCategoryId = null,
	defaultModelCategoryName = null,
	extraTopLevelOptions = EMPTY_EXTRA_TOP_LEVEL_OPTIONS,
	excludedTopLevelCategories = EMPTY_EXCLUDED_TOP_LEVEL_CATEGORIES,
}: UseCategorySelectionParams = {}) => {
	const { data: objectsCategoriesData } = useObjectsCategories()

	const [selectedTopLevelCategory, setSelectedTopLevelCategory] = useState<CategoryValue>(defaultTopLevelCategory)
	const [selectedSuperCategory, setSelectedSuperCategory] = useState<CategoryValue>(defaultSuperCategory)
	const [selectedModelCategory, setSelectedModelCategory] = useState<CategoryValue>(defaultModelCategoryId)

	const categoriesData = useMemo(() => {
		if (!objectsCategoriesData || typeof objectsCategoriesData !== "object") return {}
		return objectsCategoriesData as ICategoriesData
	}, [objectsCategoriesData])

	const topLevelOptions = useMemo(() => {
		const excluded = new Set(excludedTopLevelCategories.map((name) => name.trim().toLowerCase()))
		const optionsFromApi = Object.keys(categoriesData)
			.filter((name) => !excluded.has(name.trim().toLowerCase()))
			.map((name) => ({ label: name, value: name }))
		const hierarchyOption = defaultTopLevelCategory
			? [{ label: defaultTopLevelCategory, value: defaultTopLevelCategory }]
			: []
		return dedupeOptions([...optionsFromApi, ...hierarchyOption, ...extraTopLevelOptions], optionKey)
	}, [categoriesData, defaultTopLevelCategory, extraTopLevelOptions, excludedTopLevelCategories])

	const superCategoryOptions = useMemo(() => {
		const selectedKey = String(selectedTopLevelCategory ?? "")
		const superCategoryData = categoriesData[selectedKey] ?? {}
		const optionsFromApi = Object.keys(superCategoryData).map((name) => ({ label: name, value: name }))

		const shouldIncludeDefault =
			defaultSuperCategory &&
			defaultTopLevelCategory &&
			String(selectedTopLevelCategory ?? "") === defaultTopLevelCategory

		const hierarchyOption = shouldIncludeDefault
			? [{ label: defaultSuperCategory, value: defaultSuperCategory }]
			: []

		return dedupeOptions([...optionsFromApi, ...hierarchyOption], optionKey)
	}, [categoriesData, defaultSuperCategory, defaultTopLevelCategory, selectedTopLevelCategory])

	const modelCategoryOptions = useMemo(() => {
		const selectedTopKey = String(selectedTopLevelCategory ?? "")
		const selectedSuperKey = String(selectedSuperCategory ?? "")
		const modelCategories = categoriesData[selectedTopKey]?.[selectedSuperKey] ?? []
		const optionsFromApi = modelCategories.map((mc) => ({ label: mc.name, value: mc.id }))

		const shouldIncludeDefault =
			defaultModelCategoryId !== null &&
			defaultModelCategoryName &&
			defaultTopLevelCategory &&
			defaultSuperCategory &&
			String(selectedTopLevelCategory ?? "") === defaultTopLevelCategory &&
			String(selectedSuperCategory ?? "") === defaultSuperCategory

		const hierarchyOption = shouldIncludeDefault
			? [{ label: defaultModelCategoryName, value: defaultModelCategoryId }]
			: []

		return dedupeOptions([...optionsFromApi, ...hierarchyOption], optionKey)
	}, [
		categoriesData,
		defaultModelCategoryId,
		defaultModelCategoryName,
		defaultSuperCategory,
		defaultTopLevelCategory,
		selectedSuperCategory,
		selectedTopLevelCategory,
	])

	// Cascading resets: top level
	useEffect(() => {
		if (selectedTopLevelCategory === null) {
			if (selectedSuperCategory !== null) setSelectedSuperCategory(null)
			if (selectedModelCategory !== null) setSelectedModelCategory(null)
			return
		}
		if (!hasOption(topLevelOptions, selectedTopLevelCategory)) {
			setSelectedTopLevelCategory(null)
		}
	}, [selectedModelCategory, selectedSuperCategory, selectedTopLevelCategory, topLevelOptions])

	// Cascading resets: super category
	useEffect(() => {
		if (selectedTopLevelCategory === null) {
			if (selectedSuperCategory !== null) setSelectedSuperCategory(null)
			return
		}
		if (selectedSuperCategory === null) return
		if (!hasOption(superCategoryOptions, selectedSuperCategory)) {
			setSelectedSuperCategory(null)
		}
	}, [selectedSuperCategory, selectedTopLevelCategory, superCategoryOptions])

	// Cascading resets: model category
	useEffect(() => {
		if (selectedTopLevelCategory === null || selectedSuperCategory === null) {
			if (selectedModelCategory !== null) setSelectedModelCategory(null)
			return
		}
		if (selectedModelCategory === null) return
		if (!hasOption(modelCategoryOptions, selectedModelCategory)) {
			setSelectedModelCategory(null)
		}
	}, [modelCategoryOptions, selectedModelCategory, selectedSuperCategory, selectedTopLevelCategory])

	const handleTopLevelChange = useCallback((value: CategoryValue) => setSelectedTopLevelCategory(value), [])
	const handleSuperCategoryChange = useCallback((value: CategoryValue) => setSelectedSuperCategory(value), [])
	const handleModelCategoryChange = useCallback((value: CategoryValue) => setSelectedModelCategory(value), [])

	const resetAll = useCallback(() => {
		setSelectedTopLevelCategory(null)
		setSelectedSuperCategory(null)
		setSelectedModelCategory(null)
	}, [])

	const syncDefaults = useCallback(() => {
		setSelectedTopLevelCategory(defaultTopLevelCategory)
		setSelectedSuperCategory(defaultSuperCategory)
		setSelectedModelCategory(defaultModelCategoryId)
	}, [defaultTopLevelCategory, defaultSuperCategory, defaultModelCategoryId])

	const selectedModelCategoryId = selectedModelCategory !== null ? Number(selectedModelCategory) : null

	return {
		selectedTopLevelCategory,
		selectedSuperCategory,
		selectedModelCategory,
		selectedModelCategoryId: Number.isNaN(selectedModelCategoryId) ? null : selectedModelCategoryId,
		topLevelOptions,
		superCategoryOptions,
		modelCategoryOptions,
		handleTopLevelChange,
		handleSuperCategoryChange,
		handleModelCategoryChange,
		resetAll,
		syncDefaults,
		isAnyCategorySelected:
			selectedTopLevelCategory !== null || selectedSuperCategory !== null || selectedModelCategory !== null,
	}
}
